import type { JSX, ReactNode } from 'react'
import { useCallback, useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { IconButton } from './Button'

/**
 * Модальное окно: `role="dialog"`, `aria-modal`, Escape, ловушка фокуса и
 * возврат фокуса на элемент-инициатор. Без внешних зависимостей —
 * поведение целиком в этом файле, чтобы его можно было проверить тестом.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  /** Кнопки внизу справа. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Скрыть крестик — когда закрытие только через явные кнопки. */
  hideClose?: boolean
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose = false
}: DialogProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<Element | null>(null)
  const titleId = useId()
  const descriptionId = `${titleId}-description`
  const { t } = useTranslation()

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current
    if (panel === null) return []
    return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
  }, [])

  useEffect(() => {
    if (!open) return
    returnFocusRef.current = document.activeElement
    const first = focusables()[0] ?? panelRef.current
    first?.focus()
    return () => {
      const target = returnFocusRef.current
      if (target instanceof HTMLElement) target.focus()
    }
  }, [open, focusables])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (first === undefined || last === undefined) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose, focusables])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-overlay flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        className={cn(
          'w-full rounded-lg border border-border bg-surface shadow-3',
          size === 'sm' && 'max-w-100',
          size === 'md' && 'max-w-140',
          size === 'lg' && 'max-w-200'
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="font-display text-lg font-semibold text-fg">
              {title}
            </h2>
            {description === undefined ? null : (
              <p id={descriptionId} className="text-base text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {hideClose ? null : (
            <IconButton size="sm" aria-label={t('common.close')} onClick={onClose}>
              <X aria-hidden className="size-4" />
            </IconButton>
          )}
        </div>
        <div className="px-6 py-5">{children}</div>
        {footer === undefined ? null : (
          <div className="flex flex-wrap justify-end gap-3 border-t border-border px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
